
'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import AgGrid from '../common/AgGrid';
import Icon from '../common/Icon';
import {
    GetJobFiles,
    UploadJobFile,
    DeleteJobFile,
    GetDocumentTypesDropdown
} from '../../services/accountService';
import SuccessMessage from '../common/SuccessMessage';
import CommonModalFiles from '../common/CommonModalFiles';


const FilesTab = ({ numericJobId, jobCode }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasUploadedFile, setHasUploadedFile] = useState(false);
    const [uploadData, setUploadData] = useState({
        documentType: '',
        fileName: ''
    });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [files, setFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileErrors, setFileErrors] = useState([]);      // ✅ NEW: UI validation
    const [apiErrors, setApiErrors] = useState('');       // ✅ NEW: API errors
    const gridRef = useRef(null);
    const [dropdownFileId, setDropdownFileId] = useState(null); // NEW: Dropdown state
    const [actionMenu, setActionMenu] = useState({ isOpen: false, position: { x: 0, y: 0 }, fileId: null });
    const actionMenuRef = useRef(null);
    const [successMessage, setSuccessMessage] = useState('');


    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [fileToRemoveIndex, setFileToRemoveIndex] = useState(null);


    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    // VALIDATION RULES (exact match to your API)
    const VALIDATION_RULES = {
        allowedExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png'],
        maxSizeMB: 50
    };

    const validateFiles = (files) => {
        const errors = [];
        files.forEach((file, index) => {
            const fileIndex = index + 1;
            if (file.size === 0) {
                errors.push(`File ${fileIndex}: "${file.name}" is empty`);
                return;
            }

            const sizeMB = (file.size / 1024 / 1024).toFixed(1);
            if (file.size > VALIDATION_RULES.maxSizeMB * 1024 * 1024) {
                errors.push(`File ${fileIndex}: "${file.name}" (${sizeMB}MB) exceeds 50MB limit`);
                return;
            }

            const ext = file.name.split('.').pop().toLowerCase();
            if (!VALIDATION_RULES.allowedExtensions.includes(ext)) {
                errors.push(`File ${fileIndex}: "${file.name}" (.${ext}) not allowed. Use: ${VALIDATION_RULES.allowedExtensions.slice(0, 5).join(', ')}...`);
            }
        });
        setFileErrors(errors);
        return errors.length === 0;
    };

    // ========== LOAD DATA ==========
    const loadDocumentTypes = useCallback(async () => {
        try {
            const data = await GetDocumentTypesDropdown();
            const options = (data || []).map(dt => ({
                value: dt.documentTypeID,
                label: dt.documentType
            }));
            setDocumentTypes(options);
        } catch (error) {
            console.error('Error loading document types:', error);
        }
    }, []);

    const loadFiles = useCallback(async () => {
        if (!numericJobId) return;
        setLoadingFiles(true);
        try {
            const data = await GetJobFiles(numericJobId);
            setFiles(data || []);
            setHasUploadedFile((data || []).length > 0);
        } catch (error) {
            console.error('Error loading files:', error);
        } finally {
            setLoadingFiles(false);
        }
    }, [numericJobId]);

    useEffect(() => {
        loadDocumentTypes();
        loadFiles();
    }, [loadDocumentTypes, loadFiles]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        setFileErrors([]);
        setApiErrors('');

        if (files.length > 0) {
            setUploadData(prev => ({
                ...prev,
                fileName: files.map(f => f.name).join(', ')
            }));
            validateFiles(files);
        }
    };

    // ========== COMPLETE MULTIPLE FILE UPLOAD WITH VALIDATION ==========
    const handleSubmit = async (formData) => {
        // ✅ UI VALIDATION FIRST
        if (!numericJobId) {
            alert('Job ID missing');

            throw new Error('Job ID missing');

            return;
        }

        if (selectedFiles.length === 0) {
            alert('Please select files first');

            throw new Error('Please select files first');
            return;
        }

        if (!formData.documentType || !parseInt(formData.documentType)) {
            alert('Please select document type');

            throw new Error('Please select document type');

            return;
        }

        // if (fileErrors.length > 0) {
        //     alert(`Please fix ${fileErrors.length} validation error(s) first ${fileErrors}`);

        //     return;
        // }

        setUploading(true);
        setApiErrors('');

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('type', formData.documentType);

            // ✅ UPLOAD ALL SELECTED FILES
            selectedFiles.forEach(file => {
                formDataToSend.append('files', file);
            });

            const response = await UploadJobFile(numericJobId, formDataToSend);

            // ✅ SUCCESS
            setIsModalOpen(false);
            setSelectedFiles([]);
            setUploadData({ documentType: '', fileName: '' });
            setFileErrors([]);
            await loadFiles();
            setSuccessMessage(`${selectedFiles.length} file(s) uploaded successfully!`);

        } catch (error) {
            console.error('Upload failed:', error);

            // ✅ HANDLE API VALIDATION ERRORS (matches your API response)
            if (error.response?.data) {
                const apiData = error.response.data;
                if (apiData.errors && Array.isArray(apiData.errors)) {
                    // Multiple errors array from API
                    const errorMsg = apiData.errors.join('\n');


                    setApiErrors(errorMsg);

                    alert(`${errorMsg}`);
                } else if (apiData.message) {
                    // Single message
                    setApiErrors(apiData.message);
                    alert(apiData.message);
                } else {
                    alert('Upload failed - server error');
                }
            } else {
                alert(`Upload failed: ${error.message || 'Network error'}`);
            }
        } finally {
            setUploading(false);
        }
    };
    // const handleSoftDelete = async (fileId) => {
    //     if (!window.confirm(`Are you sure you want to delete this file?`)) return;
    //     try {
    //         await DeleteJobFile(numericJobId, fileId);
    //         setSuccessMessage('File deleted successfully!');
    //         await loadFiles();
    //     } catch (error) {
    //         console.error('Delete failed:', error);
    //         alert('❌ Delete failed!');
    //     }
    // };


    const handleSoftDelete = (fileId) => {
        setFileToDelete(fileId);
        setShowDeleteConfirm(true);
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!fileToDelete) return;

        setDeleteInProgress(true);

        try {
            await DeleteJobFile(numericJobId, fileToDelete);
            setSuccessMessage('File deleted successfully!');
            await loadFiles();
        } catch (error) {
            console.error('Delete failed:', error);
            alert('❌ Delete failed!');
        } finally {
            setDeleteInProgress(false);
            setShowDeleteConfirm(false);
            setFileToDelete(null);
        }
    };

    // 🔽 Action menu handlers
    const handleActionClick = (e, file) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setActionMenu({
            isOpen: true,
            position: { x: rect.right + window.scrollX - 120, y: rect.bottom + window.scrollY },
            fileId: file.fileID
        });
    };

    const handleConfirmRemove = () => {
        if (fileToRemoveIndex === null) return;

        setSelectedFiles(prev =>
            prev.filter((_, i) => i !== fileToRemoveIndex)
        );

        setShowRemoveConfirm(false);
        setFileToRemoveIndex(null);
    };

    const handleCancelRemove = () => {
        setShowRemoveConfirm(false);
        setFileToRemoveIndex(null);
    };


    // const handleDeleteFromMenu = async () => {
    //     if (!actionMenu.fileId) return;
    //     setActionMenu({ ...actionMenu, isOpen: false });
    //     await handleSoftDelete(actionMenu.fileId);
    // };

    const handleDeleteFromMenu = async () => {
        if (!actionMenu.fileId) return;

        setActionMenu({ ...actionMenu, isOpen: false });

        handleSoftDelete(actionMenu.fileId); // ✅ changed
    };

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
                setActionMenu(prev => ({ ...prev, isOpen: false }));
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // 🔽 Action icon renderer
    const EditIconRenderer = (params) => {
        const file = params.data;
        return (
            <div className='edit-icon-render'>
                <div
                    onClick={(e) => handleActionClick(e, file)}
                    className='action-icon'
                    style={{ cursor: 'pointer' }}
                >
                    <Icon name="action" size={16} width={3} alt="action" />
                </div>
            </div>
        );
    };

    const renderActionMenu = () => {
        if (!actionMenu.isOpen) return null;
        return (
            <div
                ref={actionMenuRef}
                style={{
                    position: 'absolute',
                    top: actionMenu.position.y,
                    left: actionMenu.position.x,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '120px',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <button
                    onClick={handleDeleteFromMenu}
                    style={{
                        padding: '8px 12px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#e74c3c'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                    Delete
                </button>
            </div>
        );
    };

    // AG-GRID CONFIG
    const columnDefs = [
        {
            field: 'fileName',
            headerName: 'File Name',
            flex: 1.5,
            sortable: true,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                const downloadUrl = params.data?.downloadUrl;
                const fileName = params.data?.fileName || '';
                const displayName = fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName;

                return (
                    <span
                        className="fw-semibold text-primary"
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: '0px 8px',
                            display: 'inline-block'
                        }}
                        title={fileName.length > 25 ? fileName : ''}
                        onClick={() => {
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = fileName;
                            // link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                    >
                        {displayName}
                    </span>
                );
            }
        },
        { field: 'uploadedBy', headerName: 'Uploaded By', flex: 1.2, sortable: true, filter: 'agTextColumnFilter', },
        { field: 'fileType', headerName: 'File Type', flex: 1, sortable: true, filter: 'agTextColumnFilter', },
        {
            field: 'uploadedAt',
            headerName: 'Uploaded Date',
            flex: 1,
            sortable: true,
            comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                if (isNaN(date.getTime())) return '';
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            }
        },
        {
            headerName: 'Action',
            field: 'edit',
            filter: false,
            width: 100,
            maxWidth: 100,
            minWidth: 80,
            suppressSizeToFit: true,
            cellRenderer: EditIconRenderer,
            cellStyle: { textAlign: 'center' }
        }
    ];

    const modalFields = [
        [
            {
                name: 'titleDisplay',
                type: 'custom',
                component: () => (
                    <div className="modal-title-section">
                        <p className="modal-subtitle">Select document type and upload file(s)</p>
                    </div>
                )
            }
        ],
        [
            {
                name: 'fileInput',
                type: 'custom',
                component: () => (
                    <div className="mb-3">
                        <label className="form-label">
                            Select Files <span className="required-asterisk">*</span>
                        </label>
                        <input type="file" className="form-control" multiple onChange={handleFileSelect} />
                        {selectedFiles.length > 0 && (
                            <div className="mt-2 p-2 bg-light rounded">
                                <small>
                                    <strong>📁 {selectedFiles.length} file(s) selected:</strong>
                                    <ul className="list-unstyled mb-0 small mt-1">
                                        {selectedFiles.map((file, index) => (
                                            <li key={index} className="py-1 d-flex justify-content-between align-items-center">
                                                <span>
                                                    📄 {file.name}
                                                    <span className="text-muted ms-2">
                                                        ({(file.size / 1024).toFixed(1)} KB)
                                                    </span>
                                                </span>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger ms-2 p-1"
                                                    style={{ lineHeight: 1 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFileToRemoveIndex(index);
                                                        setShowRemoveConfirm(true);
                                                    }}
                                                >
                                                    🗑
                                                </button>

                                            </li>
                                        ))}
                                    </ul>
                                </small>
                            </div>
                        )}
                        {fileErrors.length > 0 && (
                            <div className="mt-2 p-2 bg-danger bg-opacity-10 border border-danger rounded small">
                                <strong>⚠️ {fileErrors.length} Validation Error(s):</strong>
                                <ul className="mb-0 mt-1">
                                    {fileErrors.map((error, i) => (
                                        <li key={i} className="text-danger mb-0">{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {apiErrors && (
                            <div className="mt-2 p-2 bg-warning bg-opacity-10 border border-warning rounded small">
                                <strong>⚠️ Server Error:</strong> {apiErrors}
                            </div>
                        )}
                    </div>
                )
            }
        ],
        [
            {
                name: 'documentType',
                label: 'Document Type ',
                type: 'select',
                //  required: true,
                options: documentTypes
            }
        ]
    ];

    return (
        <>
            <div className="files-tab-container">
                {!hasUploadedFile && (
                    <div className="files-upload-section">
                        <div className="upload-area">
                            <p>No file(s) uploaded yet.</p>
                            <button className="browse-button" onClick={() => setIsModalOpen(true)}>
                                Upload Files
                            </button>
                        </div>
                    </div>
                )}
                {hasUploadedFile && (
                    <div className="files-tab-container-upload-files">
                        <div className="upload-file-buton mb-3">
                            <button className="upliad-files-btn" onClick={() => setIsModalOpen(true)}>
                                Upload File(s)
                            </button>
                        </div>
                        <AgGrid
                            enableSearch={false}
                            enableSorting={false}
                            enableFilter={false}
                            pagination={true}
                            height="500px"
                            columnDefs={columnDefs}
                            rowData={files}
                            loading={loadingFiles}
                            hideSelectAll={true}
                            hidePivotMode={true}
                            hideExcelExport={true}
                            hideColumnMenu={true}
                            hideExportButton={true}
                            hideSideBar={true}
                        />
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="delete-confirmation-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <div className="delete-confirmation-modal" style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            maxWidth: '400px',
                            width: '90%'
                        }}>
                            <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>
                            <p style={{ marginBottom: '20px' }}>Are you sure you want to delete this file?</p>
                            <div className="delete-confirmation-buttons" style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px'
                            }}>
                                <button
                                    className="cancel-delete-btn"
                                    onClick={handleCancelDelete}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#f0f0f0',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                    disabled={deleteInProgress}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="confirm-delete-btn"
                                    onClick={handleConfirmDelete}
                                    disabled={deleteInProgress}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: deleteInProgress ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {deleteInProgress ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showRemoveConfirm && (
                <div className="delete-confirmation-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        maxWidth: '400px',
                        width: '90%'
                    }}>
                        <h3 style={{ marginBottom: '15px' }}>Confirm Remove</h3>

                        <p style={{ marginBottom: '20px' }}>
                            Are you sure you want to remove this file?
                        </p>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <button
                                onClick={handleCancelRemove}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleConfirmRemove}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <CommonModalFiles
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedFiles([]);
                    setFileErrors([]);
                    setApiErrors('');
                }}
                onSave={handleSubmit}
                title="Upload File(s)"
                fields={modalFields}
                initialData={uploadData}
                submitText={uploading ? 'Uploading...' : 'Upload'}
                isSubmitting={uploading}
                width="600px"
            />
            {renderActionMenu()}

            {successMessage && (
                <SuccessMessage
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />

            )}
        </>
    );
};

export default FilesTab;
